import { BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";
import profileImage from "@/assets/profile.jpg";
import reel1 from "@/assets/reel-1.jpg";
import reel2 from "@/assets/reel-2.jpg";
import reel3 from "@/assets/reel-3.jpg";
import { BaseTemplate } from "@/components/templates/BaseTemplate";
import { BioLinkTemplate } from "@/components/templates/BioLinkTemplate";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useSEO } from "@/hooks/useSEO";

type MediaItem = {
  id: number;
  type: "video" | "photo";
  src: string;
  embedId?: string;  // For Streamable videos
  cloudflareId?: string;  // For Cloudflare Stream videos
  title: string;
};

const mediaItems: MediaItem[] = [
  { 
    id: 1, 
    type: "video", 
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/55af92c0a6afac58e780413127c73c5c/thumbnails/thumbnail.jpg", 
    cloudflareId: "55af92c0a6afac58e780413127c73c5c", 
    title: "" 
  },
  { 
    id: 2, 
    type: "video", 
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/27cbea077c11d9cc0569774b8c443a04/thumbnails/thumbnail.jpg", 
    cloudflareId: "27cbea077c11d9cc0569774b8c443a04", 
    title: "" 
  },
  { 
    id: 3, 
    type: "video", 
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/55af92c0a6afac58e780413127c73c5c/thumbnails/thumbnail.jpg", 
    cloudflareId: "55af92c0a6afac58e780413127c73c5c", 
    title: "" 
  },
  {
    id: 4,
    type: "photo",
    src: reel1,
    title: "Digital Content"
  },
  {
    id: 5,
    type: "photo",
    src: reel2,
    title: "Creative Process"
  },
  {
    id: 6,
    type: "photo",
    src: profileImage,
    title: "Digital Creator Portrait"
  },
  {
    id: 7,
    type: "video",
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/27cbea077c11d9cc0569774b8c443a04/thumbnails/thumbnail.jpg",
    cloudflareId: "27cbea077c11d9cc0569774b8c443a04",
    title: ""
  },
  {
    id: 8,
    type: "photo",
    src: reel3,
    title: "Digital Innovation"
  },
  {
    id: 9,
    type: "photo",
    src: "https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/e9f2e4fc-fbf1-4ead-6e36-c8d2b9917d00/public",
    title: "Digital Lifestyle"
  },
  {
    id: 10,
    type: "photo",
    src: "https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/5693d23a-20a6-4e89-9cb9-806ca3ccc900/public",
    title: "Creator Portrait"
  },
  {
    id: 11,
    type: "photo",
    src: "https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/2fe5d4c8-fefd-49a3-c53b-6736465a1b00/public",
    title: "Digital Studio"
  },
  {
    id: 12,
    type: "video",
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/0599f80309c4103f7ebe835cf09f5a41/thumbnails/thumbnail.jpg",
    cloudflareId: "0599f80309c4103f7ebe835cf09f5a41",
    title: ""
  },
  {
    id: 13,
    type: "video",
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/905a888c2c2c99b75ac5c03f3ab98aaa/thumbnails/thumbnail.jpg",
    cloudflareId: "905a888c2c2c99b75ac5c03f3ab98aaa",
    title: ""
  },
];

export const DigitalCreator = () => {
  const { region, shouldUseBaseTemplate, shouldUseBioLinkTemplate } = useTemplateSelection();

  // Enhanced SEO configuration with Digital Creator focus
  const seoConfig = {
    title: region === "US" 
      ? `${BRAND_PERSON_NAME} | Digital Creator & Multi-Platform Content Specialist`
      : `${BRAND_PERSON_NAME} - Digital Creator | Creadora de Contenido Digital Argentina`,
    description: region === "US" 
      ? `Join digital creator ${BRAND_PERSON_NAME}'s exclusive community! Get access to premium digital content, multi-platform strategies, and connect directly. Your gateway to innovative digital content creation.`
      : `${BRAND_PERSON_NAME} creadora de contenido digital. Especialista en contenido multiplataforma, estrategias digitales e innovación creativa en redes sociales.`,
    keywords: region === "US"
      ? `${BRAND_PERSON_NAME}, digital creator, content creator, multi-platform content, premium digital content, exclusive access, digital innovation, content strategy, social media creator`
      : `creadora digital, ${BRAND_PERSON_NAME} digital, contenido digital, multiplataforma, estrategia digital, innovacion digital, creadora contenido, redes sociales`,
    ogImage: region === "US" 
      ? BRAND_OG_IMAGE_URL
      : BRAND_OG_IMAGE_URL,
    ogTitle: region === "US"
      ? `${BRAND_PERSON_NAME} | Digital Creator & Multi-Platform Content Specialist`
      : `${BRAND_PERSON_NAME} - Digital Creator`,
    ogDescription: region === "US"
      ? "Join my exclusive community! Premium digital content, innovative strategies, and direct connection. Let's create the future of digital content together! 💻✨"
      : "Creadora de contenido digital especializada en innovación y estrategias multiplataforma. Contenido auténtico que conecta en la era digital.",
    canonical: `${BRAND_SITE_URL}/digital-creator`,
    author: BRAND_PERSON_NAME,
    publishedTime: "2026-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
    type: region === "US" ? "profile" : "website",
    locale: region === "US" ? "en_US" : "es_AR",
    alternateLocale: region === "US" ? "es_AR" : "en_US",
  };

  useSEO(seoConfig);

  // Use template based on feature flags
  if (shouldUseBioLinkTemplate()) {
    return <BioLinkTemplate mediaItems={mediaItems} />;
  }

  // Default to BaseTemplate (LATAM style)
  return <BaseTemplate mediaItems={mediaItems} />;
};

export default DigitalCreator;